import { z } from 'zod'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  breakoutsQueryOptions,
  indexSymbolsQueryOptions,
  signalSessionsQueryOptions,
} from '@/features/signals/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import type { BreakoutsQuery } from '@/schemas/signals'
import { breakoutDirectionParamSchema, sessionDateParamSchema } from '@/screens/signals/search'

/**
 * - `direction` — Pushed above (`above`) or Fell below (`below`).
 * - `date` — the session; absent is mm-be's latest.
 */
export const breakoutsSearchSchema = z.object({
  direction: breakoutDirectionParamSchema,
  date: sessionDateParamSchema,
})
export type BreakoutsSearch = z.infer<typeof breakoutsSearchSchema>

export const breakoutsQueryOf = (search: BreakoutsSearch): BreakoutsQuery => ({
  direction: search.direction,
  date: search.date ?? null,
})

/**
 * The `/market/signals/breakouts` loader. MAIN: the entry URL's board.
 * SECONDARY: the sessions, and the index symbols (which rows link).
 */
export async function loadBreakouts({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const search = breakoutsSearchSchema.parse(location.search)
  if (!preload) {
    prefetchRouteData(queryClient, signalSessionsQueryOptions())
    prefetchRouteData(queryClient, indexSymbolsQueryOptions())
  }
  await ensureRouteData(queryClient, breakoutsQueryOptions(breakoutsQueryOf(search)))
}
